import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { nursesApi } from '../lib/api/nurses';

interface NurseLabSampleCollectionProps {
  onBack: () => void;
  selectedPatient?: any;
}

export function NurseLabSampleCollection({ onBack, selectedPatient }: NurseLabSampleCollectionProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sampleData, setSampleData] = useState({
    sampleType: '',
    tubeType: '',
    volume: '',
    collectionTime: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
    labDestination: '',
    requisitionNumber: '',
    fastingStatus: '',
    specialInstructions: ''
  });

  const [checklist, setChecklist] = useState({
    patientIdentified: false,
    patientFasting: false,
    consentObtained: false,
    handsWashed: false,
    glovesWorn: false,
    equipmentSanitized: false,
    sampleLabeled: false,
    patientInformed: false,
    sampleStored: false,
    biohazardDisposal: false
  });

  const [photoTaken, setPhotoTaken] = useState(false);

  const handleTakePhoto = () => {
    toast.info('Opening camera...');
    setTimeout(() => {
      setPhotoTaken(true);
      toast.success('Sample photo captured');
    }, 1000);
  };

  const handleSubmit = async () => {
    const requiredChecklist = [
      'patientIdentified',
      'consentObtained',
      'handsWashed',
      'glovesWorn',
      'sampleLabeled',
      'sampleStored'
    ];

    const checklistComplete = requiredChecklist.every(
      key => checklist[key as keyof typeof checklist]
    );

    if (!checklistComplete) {
      toast.error('Please complete all required checklist items');
      return;
    }

    if (!sampleData.sampleType || !sampleData.tubeType || !sampleData.labDestination) {
      toast.error('Please fill in all required sample details');
      return;
    }

    if (!photoTaken) {
      toast.error('Please take a photo of the labeled sample');
      return;
    }

    if (!selectedPatient?.id) {
      toast.error('No patient selected');
      return;
    }

    setIsSubmitting(true);
    
    try {
      await nursesApi.submitLabSample({
        patient_id: selectedPatient.id,
        sample_type: sampleData.sampleType,
        tube_type: sampleData.tubeType,
        volume: sampleData.volume,
        collection_time: sampleData.collectionTime,
        lab_destination: sampleData.labDestination,
        requisition_number: sampleData.requisitionNumber || undefined,
        fasting_status: sampleData.fastingStatus || undefined,
        special_instructions: sampleData.specialInstructions || undefined,
        photo_url: photoTaken ? 'sample_photo_captured.jpg' : undefined
      });

      toast.success('Sample collection completed!', {
        description: 'Sample data saved and ready for transport'
      });

      setTimeout(() => {
        onBack();
      }, 1500);
    } catch (error: any) {
      toast.error('Failed to save sample data', {
        description: error?.message || 'Please try again'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#D4F0F8] pb-20">
      {/* Header */}
      <div className="bg-primary p-6 rounded-b-3xl shadow-md">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="text-primary-foreground">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-primary-foreground">Lab Sample Collection</h1>
            {selectedPatient ? (
              <p className="text-primary-foreground/80">
                Patient: {selectedPatient.name} ({selectedPatient.id})
              </p>
            ) : (
              <p className="text-primary-foreground/80">No patient selected</p>
            )}
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Sample Information */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="text-primary flex items-center gap-2">
              <Droplets className="w-5 h-5" />
              Sample Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Sample Type *</Label>
                <Select value={sampleData.sampleType} onValueChange={(v) => setSampleData({ ...sampleData, sampleType: v })}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select sample type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="blood">Blood</SelectItem>
                    <SelectItem value="serum">Serum</SelectItem>
                    <SelectItem value="plasma">Plasma</SelectItem>
                    <SelectItem value="urine">Urine</SelectItem>
                    <SelectItem value="sputum">Sputum</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Collection Tube Type *</Label>
                <Select value={sampleData.tubeType} onValueChange={(v) => setSampleData({ ...sampleData, tubeType: v })}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select tube type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="edta">EDTA (Purple cap)</SelectItem>
                    <SelectItem value="serum">Serum (Red cap)</SelectItem>
                    <SelectItem value="fluoride">Fluoride (Gray cap)</SelectItem>
                    <SelectItem value="citrate">Citrate (Blue cap)</SelectItem>
                    <SelectItem value="heparin">Heparin (Green cap)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Volume Collected (mL)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={sampleData.volume}
                  onChange={(e) => setSampleData({ ...sampleData, volume: e.target.value })}
                  placeholder="Enter volume"
                  className="mt-2"
                />
              </div>

              <div>
                <Label>Collection Time</Label>
                <Input
                  type="time"
                  value={sampleData.collectionTime}
                  onChange={(e) => setSampleData({ ...sampleData, collectionTime: e.target.value })}
                  className="mt-2"
                />
              </div>

              <div>
                <Label>Lab Destination *</Label>
                <Select value={sampleData.labDestination} onValueChange={(v) => setSampleData({ ...sampleData, labDestination: v })}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select lab" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="central">Central Lab - Bangalore</SelectItem>
                    <SelectItem value="pathology">Pathology Lab - Indiranagar</SelectItem>
                    <SelectItem value="microbiology">Microbiology Lab - Koramangala</SelectItem>
                    <SelectItem value="reference">Reference Lab - HSR Layout</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Requisition Number</Label>
                <Input
                  value={sampleData.requisitionNumber}
                  onChange={(e) => setSampleData({ ...sampleData, requisitionNumber: e.target.value })}
                  placeholder="Enter req. number"
                  className="mt-2"
                />
              </div>

              <div>
                <Label>Fasting Status</Label>
                <Select value={sampleData.fastingStatus} onValueChange={(v) => setSampleData({ ...sampleData, fastingStatus: v })}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fasting">Fasting (8+ hours)</SelectItem>
                    <SelectItem value="non-fasting">Non-fasting</SelectItem>
                    <SelectItem value="unknown">Unknown</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Special Instructions / Notes</Label>
              <Textarea
                value={sampleData.specialInstructions}
                onChange={(e) => setSampleData({ ...sampleData, specialInstructions: e.target.value })}
                placeholder="Any special handling requirements, patient condition notes, etc."
                className="mt-2"
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Pre-Collection Checklist */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="text-primary">Pre-Collection Checklist</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {[
              { key: 'patientIdentified', label: 'Patient identity verified (name, ID, DOB)', required: true },
              { key: 'patientFasting', label: 'Fasting status confirmed (if applicable)' },
              { key: 'consentObtained', label: 'Informed consent obtained', required: true },
              { key: 'handsWashed', label: 'Hands washed/sanitized', required: true },
              { key: 'glovesWorn', label: 'Gloves worn', required: true },
              { key: 'equipmentSanitized', label: 'Collection equipment sanitized' }
            ].map((item) => (
              <div key={item.key} className="flex items-start gap-3 p-3 bg-secondary rounded-lg">
                <Checkbox
                  id={item.key}
                  checked={checklist[item.key as keyof typeof checklist]}
                  onCheckedChange={(checked) =>
                    setChecklist({ ...checklist, [item.key]: checked as boolean })
                  }
                />
                <label
                  htmlFor={item.key}
                  className="text-foreground cursor-pointer flex-1"
                >
                  {item.label}
                  {item.required && <span className="text-destructive ml-1">*</span>}
                </label>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Post-Collection Checklist */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="text-primary">Post-Collection Checklist</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {[
              { key: 'sampleLabeled', label: 'Sample tube properly labeled with patient info', required: true },
              { key: 'patientInformed', label: 'Patient informed about next steps' },
              { key: 'sampleStored', label: 'Sample stored at appropriate temperature', required: true },
              { key: 'biohazardDisposal', label: 'Used materials disposed in biohazard container' }
            ].map((item) => (
              <div key={item.key} className="flex items-start gap-3 p-3 bg-secondary rounded-lg">
                <Checkbox
                  id={item.key}
                  checked={checklist[item.key as keyof typeof checklist]}
                  onCheckedChange={(checked) =>
                    setChecklist({ ...checklist, [item.key]: checked as boolean })
                  }
                />
                <label
                  htmlFor={item.key}
                  className="text-foreground cursor-pointer flex-1"
                >
                  {item.label}
                  {item.required && <span className="text-destructive ml-1">*</span>}
                </label>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Sample Photo */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="text-primary flex items-center gap-2">
              <Camera className="w-5 h-5" />
              Sample Documentation
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!photoTaken ? (
              <Button
                variant="outline"
                className="w-full h-32 border-dashed"
                onClick={handleTakePhoto}
              >
                <div className="text-center">
                  <Camera className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-primary">Take Photo of Labeled Sample</p>
                  <p className="text-muted-foreground text-sm">Required for verification</p>
                </div>
              </Button>
            ) : (
              <div className="bg-secondary rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-16 h-16 bg-primary/20 rounded-lg flex items-center justify-center">
                      <Package className="w-8 h-8 text-primary" />
                    </div>
                    <div>
                      <p className="text-foreground">Sample Photo Captured</p>
                      <p className="text-muted-foreground text-sm">
                        {new Date().toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                  <Badge className="bg-[#14CAF4] text-white">
                    <Check className="w-4 h-4 mr-1" />
                    Verified
                  </Badge>
                </div>
                <Button variant="outline" size="sm" onClick={handleTakePhoto}>
                  Retake Photo
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Alert for Transport */}
        <Card className="shadow-lg bg-accent/10 border-accent">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-accent mb-1">Transport Instructions</h4>
                <p className="text-foreground text-sm">
                  Ensure sample is transported to {sampleData.labDestination || 'the lab'} within 2 hours.
                  Maintain temperature at 2-8°C if required. Include requisition form with sample.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button variant="outline" className="flex-1" onClick={onBack}>
            Cancel
          </Button>
          <Button className="flex-1 bg-accent hover:bg-accent/90" onClick={handleSubmit} disabled={isSubmitting}>
            <Save className="w-4 h-4 mr-2" />
            Complete Collection
          </Button>
        </div>
      </div>
    </div>
  );
}